import { Component, ChangeDetectionStrategy, inject, computed, signal, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { Actions, ofType } from "@ngrx/effects";
import { TranslateModule } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { StepperModule } from "primeng/stepper";

import { QuoteActions } from "src/app/store/pages/quote/quote.actions";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";
import { selectNeedsCreditCheckStep } from "src/app/store/application/data/credit-check/quote/quote-credit-check.selectors";
import { CurrencyWarningDialogComponent } from "src/app/pages/quote/dialog/currency-warning-dialog/currency-warning-dialog.component";
import { CreditCheckStepComponent } from "../credit-check-step/credit-check-step.component";
import { SendQuoteDialogComponent } from "../send-quote-dialog/send-quote-dialog.component";

/**
 * "Skicka offert" as one dialog with a stepper, replacing the chain of popups that used to open
 * one after another.
 *
 * Both leading steps are conditional, so a quote with a steady exchange rate and a valid credit
 * check goes straight to the send form and the stepper header is not shown at all.
 *
 * The currency step is the reason this exists as much as the tidiness: it used to be a dead end —
 * both of its buttons closed the dialog and the user had to click "Skicka offert" again.
 */
@Component({
    selector: "app-send-quote-stepper",
    templateUrl: "./send-quote-stepper.component.html",
    styleUrl: "../quote-flow-stepper.scss",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonModule,
        StepperModule,
        TranslateModule,
        CurrencyWarningDialogComponent,
        CreditCheckStepComponent,
        SendQuoteDialogComponent,
    ],
})
export class SendQuoteStepperComponent {

    private readonly ref = inject(DynamicDialogRef);
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);

    private readonly sendForm = viewChild(SendQuoteDialogComponent);

    // =========================================================================
    // WHICH STEPS APPLY
    //
    // Captured once on open. If they tracked the store live, resolving a step would renumber the
    // stepper underneath the user mid-flow.
    // =========================================================================
    public readonly showCurrencyStep = signal(
        this.store.selectSignal(selectVersion)()?.exchangeInfo?.diffIsBig === true,
    );
    public readonly showCreditStep = signal(this.store.selectSignal(selectNeedsCreditCheckStep)());

    /** Step numbers are assigned in order so a skipped leading step doesn't leave a gap. */
    public readonly currencyStepIndex = computed(() => 1);
    public readonly creditStepIndex = computed(() => (this.showCurrencyStep() ? 2 : 1));
    public readonly sendStepIndex = computed(() =>
        1 + (this.showCurrencyStep() ? 1 : 0) + (this.showCreditStep() ? 1 : 0),
    );

    /** With no leading steps there is nothing to navigate, so the header is just noise. */
    public readonly showStepHeader = computed(() => this.showCurrencyStep() || this.showCreditStep());

    public readonly activeStep = signal(1);

    public readonly canSend = computed(() => this.sendForm()?.canSend() ?? false);

    constructor() {
        // The send itself is dispatched by the embedded form; close once it has been accepted.
        this.actions$.pipe(ofType(QuoteActions.send), takeUntilDestroyed())
            .subscribe(() => this.ref.close());
    }

    // =========================================================================
    // NAVIGATION
    // =========================================================================
    public goToStep(step: number): void {
        this.activeStep.set(step);
    }

    /** Called by each leading step when the user has dealt with it. */
    public advanceFrom(step: number): void {
        this.activeStep.set(step + 1);
    }

    public submit(): void {
        this.sendForm()?.onSubmit();
    }

    public cancel(): void {
        this.ref.close();
    }
}
